import { useEffect } from 'preact/hooks';
import type { Product } from '@/domain/Product.ts';
import ErrorBoundary from '@/widgets/components/containers/ErrorBoundary/ErrorBoundary.tsx';
import ProductLabelSection from '../sections/ProductLabelSection/ProductLabelSection.tsx';
import ProductNameSection from '../sections/ProductNameSection/ProductNameSection.tsx';
import ProductFeaturesSection from '../sections/ProductFeaturesSection/ProductFeaturesSection.tsx';
import ProductMediaSection from '../sections/ProductMediaSection/ProductMediaSection.tsx';
import ProductVariantsSection from '../sections/ProductVariantsSection/ProductVariantsSection.tsx';
import PurchaseDriversSection from '../sections/PurchaseDriversSection/PurchaseDriversSection.tsx';
import PurchaseSection from '../sections/PurchaseSection/PurchaseSection.tsx';
import ProductJsonLd from '@/widgets/components/jsonld/ProductJsonLd/ProductJsonLd.tsx';
import ProductCompatibilitySection from '../sections/ProductCompatibilitySection/ProductCompatibilitySection.tsx';
import styles from './HeroLayout.module.scss';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import type { Options } from '../config/index.ts';
import OtherWaysToShopSection from '../sections/OtherWaysToShopSection/OtherWaysToShopSection.tsx';
import { trackPageBuyOptions } from '../Tracking.ts';
import NotificationContainer from '@/widgets/components/controls/Notifications/NotificationContainer/NotificationContainer.tsx';

export type HeroLayoutProps = {
  product: Product;
};

export default function HeroLayout({ product }: HeroLayoutProps) {
  const { hideProductCompatibilitySection, buyOptionsPlacement } =
    useWidgetOptions<Options>();

  useEffect(() => {
    trackPageBuyOptions(buyOptionsPlacement, product);
  }, [buyOptionsPlacement, product]);

  return (
    <article class={styles.container}>
      <ErrorBoundary>
        <ProductJsonLd product={product} />
      </ErrorBoundary>
      <ErrorBoundary>
        <ProductLabelSection product={product} />
      </ErrorBoundary>
      <ErrorBoundary>
        <ProductNameSection product={product} itemListName="Hero Layout" />
      </ErrorBoundary>
      <ErrorBoundary>
        <ProductFeaturesSection product={product} />
      </ErrorBoundary>
      <ErrorBoundary>
        <ProductMediaSection class={styles.media} product={product} />
      </ErrorBoundary>
      <ErrorBoundary>
        {!hideProductCompatibilitySection && (
          <ProductCompatibilitySection product={product} />
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        <PurchaseDriversSection product={product} />
      </ErrorBoundary>
      <ErrorBoundary>
        <ProductVariantsSection product={product} />
      </ErrorBoundary>
      <ErrorBoundary>
        <PurchaseSection product={product} />
      </ErrorBoundary>
      <ErrorBoundary>
        <OtherWaysToShopSection product={product} />
      </ErrorBoundary>

      {/* uncomment if you want to see a demo of notifications  */}
      {/* <NotificationQuickDemo /> */}
      <NotificationContainer />
    </article>
  );
}

HeroLayout.Skeleton = function Skeleton() {
  return (
    <article class={styles.container} data-nosnippet>
      <ErrorBoundary>
        <ProductLabelSection.Skeleton />
        <ProductNameSection.Skeleton />
        <ProductFeaturesSection.Skeleton />
        <ProductMediaSection.Skeleton class={styles.media} />
        <ProductCompatibilitySection.Skeleton />
        <PurchaseDriversSection.Skeleton />
        <ProductVariantsSection.Skeleton />
        <PurchaseSection.Skeleton />
      </ErrorBoundary>
    </article>
  );
};
